import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  InputAdornment,
  Chip,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../../../authContext/context';
import { postRequest } from '../../../lib/api';
import BreadcrumbsWrapper from '../../../components/breadcrumbs';
import Swal from 'sweetalert2';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useForm, Controller } from 'react-hook-form';

// Customer Search Modal Component
function CustomerSearchModal({ show, onHide, authToken, onCustomerAdd, selectedCustomers, title = 'Add Purchaser' }) {
  const [searchFields, setSearchFields] = useState({
    title: '',
    forename: '',
    surname: '',
    email: '',
    phone_number: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = React.useRef();

  const fetchCustomerSearch = async () => {
    const isEmpty = Object.values(searchFields).every(v => !v);
    if (isEmpty) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      const res = await postRequest('private/leadsCustomerList', searchFields, authToken);
      setSearchResults(res?.data || []);
    } catch (err) {
      console.error('Error fetching search:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchCustomerSearch, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchFields]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchFields((prev) => ({ ...prev, [name]: value }));
  };

  const isCustomerAdded = (customerId) => {
    return selectedCustomers.some(c => c._id === customerId);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className='m-0'>
          {['title', 'forename', 'surname', 'email', 'phone_number'].map((field, idx) => (
            <Col xs={field === 'email' || field === 'phone_number' ? 6 : 4} key={idx}>
              <Box sx={{ '& > :not(style)': { m: 1, width: '100%' } }}>
                <TextField
                  label={field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ')}
                  name={field}
                  value={searchFields[field]}
                  onChange={handleChange}
                  variant="outlined"
                  type={field === 'email' ? 'email' : field === 'phone_number' ? 'tel' : 'text'}
                />
              </Box>
            </Col>
          ))}
        </Row>

        <Row className='mt-4'>
          <Col>
            <Table responsive bordered>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Add</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="4" className='text-center'>Loading...</td></tr>
                ) : searchResults.length > 0 ? (
                  searchResults.map((customer) => (
                    <tr key={customer._id}>
                      <td>{customer.title} {customer.forename} {customer.surname}</td>
                      <td>{customer.email}</td>
                      <td>{customer.phone_number}</td>
                      <td>
                        <Button
                          variant={isCustomerAdded(customer._id) ? "contained" : "outlined"}
                          size="small"
                          disabled={isCustomerAdded(customer._id)}
                          onClick={() => {
                            onCustomerAdd(customer);
                            onHide();
                          }}
                        >
                          {isCustomerAdded(customer._id) ? 'Added' : 'Add'}
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="4" className='text-center'>No customers found</td></tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

const EditConveyancing = () => {
  const { lead_id, slug, product_id } = useParams();
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  
  console.log('🔄 EditConveyancing Loading - Lead ID:', lead_id);

  const [purchasers, setPurchasers] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showSellerModal, setShowSellerModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [leadData, setLeadData] = useState(null);
  const [conveyancingData, setConveyancingData] = useState(null);
  const [partners, setPartners] = useState([]);
  const [lenderPanels, setLenderPanels] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [selectedLenderPanelId, setSelectedLenderPanelId] = useState('');

  // Info section state for clickable chips
  const [infoData, setInfoData] = useState({
    firstTimeBuyer: false,
    helpToBuyISA: false,
    helpToBuy: false,
    helpToBuyEquityLoan: false,
    rightToBuy: false,
    sharedOwnership: false,
    buyingWithMortgage: false,
    newBuild: false,
    islamicMortgage: false,
    auctionRepossession: false,
    giftedDeposit: 0,
    multipleProperties: false,
    buyToLet: false,
    arrangingIndemnityPolicy: false,
    unregisteredProperty: false,
    staircasing: false,
    isNonUKResident: false,
    nonResidentialMixed: false
  });

  const { control, handleSubmit, setValue, reset, formState: { errors } } = useForm({
    defaultValues: {
      // Property Details
      line1: '',
      line2: '',
      town: '',
      county: '',
      postcode: '',
      purchasePrice: '',
      ownershipType: '',
      completionDate: '',
      giftedDeposit: 0
    },
  });

  const handleCustomerAdd = (customer) => {
    if (!purchasers.some(p => p._id === customer._id)) {
      setPurchasers([...purchasers, customer]);
    }
  };

  const removePurchaser = (customerId) => {
    setPurchasers(purchasers.filter(p => p._id !== customerId));
  };

  const handleSellerAdd = (seller) => {
    if (!sellers.some(s => s._id === seller._id)) {
      setSellers([...sellers, seller]);
    }
  };

  const removeSeller = (sellerId) => {
    setSellers(sellers.filter(s => s._id !== sellerId));
  };

  // Toggle info data
  const toggleInfoData = (key) => {
    setInfoData(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Auto-fill lead details
  const fetchAndAutoFillData = async () => {
    try {
      setLoading(true);
      console.log('=== EDIT CONVEYANCING AUTO-FILL START ===');
      
      let lead = null;
      let apiUsed = '';
      
      // Try multiple API endpoints
      // Note: Some APIs expect ObjectId, so we prioritize APIs that work with string lead_ids
      const apiAttempts = [
        { url: `private/leadList`, payload: { lead_id: lead_id }, name: 'leadList with filter' },
        { url: `private/conveyancing-lead-overview-data/${lead_id}`, name: 'conveyancing-lead-overview-data' },
        { url: `private/lead-overview-data/${lead_id}`, name: 'lead-overview-data' },
        { url: `private/getSingleLead/${lead_id}`, name: 'getSingleLead' },
        { url: `private/get-single-lead/${lead_id}`, name: 'get-single-lead' }
      ];
      
      for (const attempt of apiAttempts) {
        try {
          console.log(`🔄 Trying API: ${attempt.name}`);
          const res = attempt.payload ? 
            await postRequest(attempt.url, attempt.payload, authCtx.token) :
            await postRequest(attempt.url, {}, authCtx.token);
          
          console.log(`📋 ${attempt.name} response:`, res);
          
          if (res?.data) {
            if (Array.isArray(res.data) && res.data.length > 0) {
              lead = res.data[0];
            } else if (res.data.docs && Array.isArray(res.data.docs) && res.data.docs.length > 0) {
              lead = res.data.docs[0];
            } else {
              lead = res.data;
            }
            
            apiUsed = attempt.name;
            console.log(`✅ SUCCESS with ${attempt.name}:`, lead);
            break;
          }
        } catch (err) {
          console.log(`❌ ${attempt.name} failed:`, err.message);
        }
      }

      if (lead) {
        console.log('✅ Lead data found, setting lead state:', lead);
        console.log('📋 Lead partner_id:', lead.partner_id);
        console.log('📋 Lead lender_panel_id:', lead.lender_panel_id);
        console.log('📋 Lead address:', lead.address || lead.property_address);
        setLeadData(lead);

        // Try to get conveyancing specific details using safe APIs
        try {
          console.log('🔄 Trying to fetch conveyancing-specific details...');
          
          // Try multiple conveyancing detail endpoints that work with string IDs
          const conveyancingAttempts = [
            { url: `private/leadList`, payload: { lead_id: lead_id, include_conveyancing: true }, name: 'leadList with conveyancing' },
            { url: `private/conveyancing-details`, payload: { lead_id: lead_id }, name: 'conveyancing-details POST' },
            { url: `private/getConveyancingPurchase`, payload: { lead_id: lead_id }, name: 'getConveyancingPurchase POST' }
          ];
          
          let conveyancingData = null;
          
          for (const attempt of conveyancingAttempts) {
            try {
              console.log(`🔄 Trying conveyancing API: ${attempt.name}`);
              const convRes = await postRequest(attempt.url, attempt.payload, authCtx.token);
              console.log(`📋 ${attempt.name} response:`, convRes);
              
              if (convRes?.status === 200 && convRes?.data) {
                console.log(`✅ Conveyancing details found via ${attempt.name}:`, convRes.data);
                conveyancingData = convRes.data;
                break;
              }
            } catch (err) {
              console.log(`❌ ${attempt.name} failed:`, err.message);
            }
          }
          
          if (conveyancingData) {
            setConveyancingData(conveyancingData);
            await performDetailedAutoFill(conveyancingData, lead);
          } else {
            console.log('ℹ️ No conveyancing details found from any endpoint, using main lead data');
            await performMainLeadAutoFill(lead);
          }
        } catch (convErr) {
          console.log('ℹ️ Error fetching conveyancing details, using lead data for auto-fill:', convErr.message);
          performMainLeadAutoFill(lead);
        }
      } else {
        console.log('❌ NO LEAD DATA FROM ANY API');
        Swal.fire('Info', 'No lead data found. Please check the lead ID.', 'info');
      }
    } catch (err) {
      console.error('❌ AUTO-FILL ERROR:', err);
      Swal.fire('Error', 'Failed to load lead data. Please try again.', 'error');
    } finally {
      setLoading(false);
      console.log('=== EDIT CONVEYANCING AUTO-FILL END ===');
    }
  };

  const performDetailedAutoFill = (convData, lead) => {
    console.log('🚀 Auto-filling from conveyancing details:', convData);
    console.log('🚀 Lead data in detailed autofill:', lead);

    // Auto-fill property details from convData or fallback to lead
    let addressToFill = {
      line1: '',
      line2: '',
      town: '',
      county: '',
      postcode: ''
    };

    if (convData.propertyDetails?.address) {
      const addr = convData.propertyDetails.address;
      addressToFill = {
        line1: addr.line1 || addr.address1 || '',
        line2: addr.line2 || addr.address2 || '',
        town: addr.town || '',
        county: addr.county || addr.country || '',
        postcode: addr.postcode || ''
      };
    } else if (lead?.address || lead?.property_address) {
      // Fallback to lead address
      const addr = lead.address || lead.property_address;
      if (typeof addr === 'object') {
        addressToFill = {
          line1: addr.line1 || addr.address1 || '',
          line2: addr.line2 || addr.address2 || '',
          town: addr.town || '',
          county: addr.county || addr.country || '',
          postcode: addr.postcode || ''
        };
      }
    }

    console.log('📍 Address to fill:', addressToFill);

    const priceValue = convData.propertyDetails?.purchasePrice || lead?.property_value || lead?.net_value || '';
    const ownershipValue = convData.propertyDetails?.ownershipType || lead?.ownership_type || 'Freehold';
    const completionValue = convData.propertyDetails?.completionDate || lead?.completion_date || '';

    console.log('💰 Price:', priceValue, 'Ownership:', ownershipValue, 'Completion:', completionValue);

    // Use reset to populate the form instead of setValue
    reset({
      line1: addressToFill.line1,
      line2: addressToFill.line2,
      town: addressToFill.town,
      county: addressToFill.county,
      postcode: addressToFill.postcode,
      purchasePrice: priceValue,
      ownershipType: ownershipValue,
      completionDate: completionValue,
      giftedDeposit: convData.info?.giftedDeposit || 0
    });

    // Force update after delay
    setTimeout(() => {
      console.log('🔄 Force updating detailed autofill...');
      setValue('line1', addressToFill.line1, { shouldValidate: true, shouldDirty: true });
      setValue('line2', addressToFill.line2, { shouldValidate: true, shouldDirty: true });
      setValue('town', addressToFill.town, { shouldValidate: true, shouldDirty: true });
      setValue('county', addressToFill.county, { shouldValidate: true, shouldDirty: true });
      setValue('postcode', addressToFill.postcode, { shouldValidate: true, shouldDirty: true });
      setValue('purchasePrice', priceValue, { shouldValidate: true, shouldDirty: true });
      setValue('ownershipType', ownershipValue, { shouldValidate: true, shouldDirty: true });
      setValue('completionDate', completionValue, { shouldValidate: true, shouldDirty: true });
    }, 1000);

    // Auto-fill info section
    if (convData.info) {
      setInfoData(prev => ({
        ...prev,
        ...convData.info
      }));
      setValue('giftedDeposit', convData.info.giftedDeposit || 0);
    }

    // Auto-fill sellers and purchasers based on service_type
    const serviceType = lead?.service_type || '';
    const isSale = serviceType === 'Sale' || serviceType === 'Purchase_Sale';
    const isPurchase = serviceType === 'Purchase' || serviceType === 'Purchase_Sale';

    console.log('📋 Conveyancing Data - Service Type:', serviceType);

    // Track what we actually set
    let sellersToSet = [];
    let purchasersToSet = [];

    // Set sellers for Sale/Purchase_Sale
    if (isSale) {
      if (convData.sellers && convData.sellers.length > 0) {
        sellersToSet = convData.sellers;
        setSellers(sellersToSet);
        console.log('✅ Sellers set from conveyancing data:', sellersToSet.length, sellersToSet);
      } else if (convData.customer_ids && Array.isArray(convData.customer_ids) && convData.customer_ids.length > 0) {
        sellersToSet = convData.customer_ids;
        setSellers(sellersToSet);
        console.log('✅ Sellers set from conveyancing customer_ids:', sellersToSet.length, sellersToSet);
      } else if (convData.customers && Array.isArray(convData.customers) && convData.customers.length > 0) {
        sellersToSet = convData.customers;
        setSellers(sellersToSet);
        console.log('✅ Sellers set from conveyancing customers:', sellersToSet.length, sellersToSet);
      } else {
        console.log('⚠️ No seller data in conveyancing data, will use lead data');
      }
    }

    // Set purchasers for Purchase/Purchase_Sale
    if (isPurchase) {
      if (convData.purchasers && convData.purchasers.length > 0) {
        purchasersToSet = convData.purchasers;
        setPurchasers(purchasersToSet);
        console.log('✅ Purchasers set from conveyancing data:', purchasersToSet.length, purchasersToSet);
      } else if (convData.customer_ids && Array.isArray(convData.customer_ids) && convData.customer_ids.length > 0) {
        purchasersToSet = convData.customer_ids;
        setPurchasers(purchasersToSet);
        console.log('✅ Purchasers set from conveyancing customer_ids:', purchasersToSet.length, purchasersToSet);
      } else if (convData.customers && Array.isArray(convData.customers) && convData.customers.length > 0) {
        purchasersToSet = convData.customers;
        setPurchasers(purchasersToSet);
        console.log('✅ Purchasers set from conveyancing customers:', purchasersToSet.length, purchasersToSet);
      } else {
        console.log('⚠️ No purchaser data in conveyancing data, will use lead data');
      }
    }

    // Partner Auto-fill
    if (lead?.partner_id || convData?.partner_id) {
      const partnerId = typeof (lead?.partner_id || convData?.partner_id) === 'object'
        ? (lead?.partner_id || convData?.partner_id)._id
        : (lead?.partner_id || convData?.partner_id);
      setSelectedPartnerId(partnerId);
      console.log('🤝 Partner ID autofilled:', partnerId);
    }

    // Lender Panel Auto-fill
    if (lead?.lender_panel_id || convData?.lender_panel_id) {
      const lenderPanelId = typeof (lead?.lender_panel_id || convData?.lender_panel_id) === 'object'
        ? (lead?.lender_panel_id || convData?.lender_panel_id)._id
        : (lead?.lender_panel_id || convData?.lender_panel_id);
      setSelectedLenderPanelId(lenderPanelId);
      console.log('🏦 Lender Panel ID autofilled:', lenderPanelId);
    }

    console.log('📊 Detailed auto-fill - Sellers:', sellersToSet.length, 'Purchasers:', purchasersToSet.length);
  };

  const performMainLeadAutoFill = async (lead) => {
    console.log('🚀 Auto-filling from main lead data:', lead);

    // Auto-fill property address - check both address and property_address
    let addressData = {
      line1: '',
      line2: '',
      town: '',
      county: '',
      postcode: ''
    };

    const addressSource = lead.address || lead.property_address;
    console.log('📍 Address source for autofill:', addressSource);

    if (addressSource) {
      if (typeof addressSource === 'object') {
        addressData.line1 = addressSource.address1 || addressSource.line1 || '';
        addressData.line2 = addressSource.address2 || addressSource.line2 || '';
        addressData.town = addressSource.town || addressSource.city || '';
        addressData.county = addressSource.county || addressSource.country || addressSource.state || '';
        addressData.postcode = addressSource.postcode || addressSource.postal_code || '';
        console.log('📍 Parsed address object:', addressData);
      } else if (typeof addressSource === 'string') {
        const parts = addressSource.split(',').map(p => p.trim());
        addressData.line1 = parts[0] || '';
        addressData.line2 = parts[1] || '';
        addressData.town = parts[2] || '';
        addressData.county = parts[3] || '';
        addressData.postcode = parts[4] || '';
        console.log('📍 Parsed address string:', addressData);
      }
    } else {
      console.log('⚠️ No address found in lead data');
    }

    // Format completion date
    let completionValue = '';
    if (lead.completion_date) {
      try {
        const date = new Date(lead.completion_date);
        if (!isNaN(date.getTime())) {
          completionValue = date.toISOString().split('T')[0];
        }
      } catch (err) {
        console.log('Date parsing error:', err);
      }
    }

    const priceValue = lead.property_value || lead.net_value || '';
    const ownershipValue = lead.ownership_type || 'Freehold';
    const giftedDepositValue = lead.gifted_deposit || 0;

    console.log('📝 Form data to set:', { addressData, priceValue, ownershipValue, completionValue, giftedDepositValue });

    // Use reset to populate the form
    reset({
      line1: addressData.line1,
      line2: addressData.line2,
      town: addressData.town,
      county: addressData.county,
      postcode: addressData.postcode,
      purchasePrice: priceValue,
      ownershipType: ownershipValue,
      completionDate: completionValue,
      giftedDeposit: giftedDepositValue
    });

    // Force update after delay to ensure form is ready
    setTimeout(() => {
      console.log('🔄 Force updating form values...');
      setValue('line1', addressData.line1, { shouldValidate: true, shouldDirty: true });
      setValue('line2', addressData.line2, { shouldValidate: true, shouldDirty: true });
      setValue('town', addressData.town, { shouldValidate: true, shouldDirty: true });
      setValue('county', addressData.county, { shouldValidate: true, shouldDirty: true });
      setValue('postcode', addressData.postcode, { shouldValidate: true, shouldDirty: true });
      setValue('purchasePrice', priceValue, { shouldValidate: true, shouldDirty: true });
      setValue('ownershipType', ownershipValue, { shouldValidate: true, shouldDirty: true });
      setValue('completionDate', completionValue, { shouldValidate: true, shouldDirty: true });

      console.log('✅ Form values force updated');
    }, 1000);

    // Auto-fill info data
    setInfoData(prev => ({
      ...prev,
      firstTimeBuyer: Boolean(lead.first_time_buyer),
      helpToBuyISA: Boolean(lead.help_to_buy_isa),
      helpToBuy: Boolean(lead.help_to_buy),
      helpToBuyEquityLoan: Boolean(lead.help_to_buy_equity_loan),
      rightToBuy: Boolean(lead.right_to_buy),
      sharedOwnership: Boolean(lead.shared_ownership),
      buyingWithMortgage: Boolean(lead.buying_with_mortgage),
      newBuild: Boolean(lead.new_build),
      islamicMortgage: Boolean(lead.islamic_mortgage),
      auctionRepossession: Boolean(lead.auction_repossession),
      giftedDeposit: lead.gifted_deposit || 0,
      multipleProperties: Boolean(lead.multiple_properties),
      buyToLet: Boolean(lead.buy_to_let),
      arrangingIndemnityPolicy: Boolean(lead.arranging_indemnity_policy),
      unregisteredProperty: Boolean(lead.unregistered_property),
      staircasing: Boolean(lead.staircasing),
      isNonUKResident: Boolean(lead.non_uk_resident || lead.is_non_uk_resident),
      nonResidentialMixed: Boolean(lead.non_residential_mixed_use)
    }));

    setValue('giftedDeposit', lead.gifted_deposit || 0);

    // Auto-fill customers based on service_type
    const serviceType = lead.service_type || '';
    const isSale = serviceType === 'Sale' || serviceType === 'Purchase_Sale';
    const isPurchase = serviceType === 'Purchase' || serviceType === 'Purchase_Sale';

    console.log('📋 Service Type:', serviceType, '| Sale:', isSale, '| Purchase:', isPurchase);
    console.log('📋 Lead customer_ids:', lead.customer_ids);
    console.log('📋 Lead customers:', lead.customers);

    // Track what we actually set (since state updates are async)
    let sellersToSet = [];
    let purchasersToSet = [];

    // For Sale - Set Sellers (same logic as Purchase form - check customers first!)
    if (isSale) {
      console.log('💼 Processing SELLERS for Sale/Purchase_Sale...');

      // FIRST: Check if lead.customers exists (full objects)
      if (lead.customers && Array.isArray(lead.customers) && lead.customers.length > 0) {
        sellersToSet = lead.customers;
        setSellers(sellersToSet);
        console.log('✅ Sellers set from customers array:', sellersToSet.length, sellersToSet);
      }
      // SECOND: Check customer_ids
      else if (lead.customer_ids && Array.isArray(lead.customer_ids) && lead.customer_ids.length > 0) {
        console.log('🔍 Fetching seller details by IDs...');
        try {
          const customerPromises = lead.customer_ids.map(async (customerId) => {
            try {
              const customerRes = await postRequest(`private/getCustomerIndividualData/${customerId}`, {}, authCtx.token);
              return customerRes?.data || null;
            } catch (err) {
              console.log('Error fetching seller:', customerId, err);
              return null;
            }
          });

          const customers = await Promise.all(customerPromises);
          sellersToSet = customers.filter(customer => customer && customer._id);
          setSellers(sellersToSet);
          console.log('✅ Sellers fetched and set:', sellersToSet.length, sellersToSet);
        } catch (err) {
          console.error('Error fetching sellers:', err);
        }
      } else {
        console.log('⚠️ No seller data found in lead');
      }
    }

    // For Purchase - Set Purchasers (same logic as Purchase form - check customers first!)
    if (isPurchase) {
      console.log('🏠 Processing PURCHASERS for Purchase/Purchase_Sale...');

      // FIRST: Check if lead.customers exists (full objects)
      if (lead.customers && Array.isArray(lead.customers) && lead.customers.length > 0) {
        purchasersToSet = lead.customers;
        setPurchasers(purchasersToSet);
        console.log('✅ Purchasers set from customers array:', purchasersToSet.length, purchasersToSet);
      }
      // SECOND: Check customer_ids
      else if (lead.customer_ids && Array.isArray(lead.customer_ids) && lead.customer_ids.length > 0) {
        console.log('🔍 Fetching purchaser details by IDs...');
        try {
          const customerPromises = lead.customer_ids.map(async (customerId) => {
            try {
              const customerRes = await postRequest(`private/getCustomerIndividualData/${customerId}`, {}, authCtx.token);
              return customerRes?.data || null;
            } catch (err) {
              console.log('Error fetching purchaser:', customerId, err);
              return null;
            }
          });

          const customers = await Promise.all(customerPromises);
          purchasersToSet = customers.filter(customer => customer && customer._id);
          setPurchasers(purchasersToSet);
          console.log('✅ Purchasers fetched and set:', purchasersToSet.length, purchasersToSet);
        } catch (err) {
          console.error('Error fetching purchasers:', err);
        }
      } else {
        console.log('⚠️ No purchaser data found in lead');
      }
    }

    // Partner Auto-fill
    if (lead?.partner_id) {
      const partnerId = typeof lead.partner_id === 'object' ? lead.partner_id._id : lead.partner_id;
      setSelectedPartnerId(partnerId);
      console.log('🤝 Partner ID autofilled:', partnerId);
    }

    // Lender Panel Auto-fill
    if (lead?.lender_panel_id) {
      const lenderPanelId = typeof lead.lender_panel_id === 'object' ? lead.lender_panel_id._id : lead.lender_panel_id;
      setSelectedLenderPanelId(lenderPanelId);
      console.log('🏦 Lender Panel ID autofilled:', lenderPanelId);
    }

    console.log('✅ Main lead auto-fill completed');
    console.log('📊 FINAL DATA SET - Sellers:', sellersToSet.length, 'Purchasers:', purchasersToSet.length);
    console.log('📊 Sellers data:', sellersToSet);
    console.log('📊 Purchasers data:', purchasersToSet);
  };

  const fetchPartners = async () => {
    try {
      const res = await postRequest('private/partnerList', { page: 1, limit: 100 }, authCtx.token);
      if (res?.status === 200 && res?.data?.docs) {
        setPartners(res.data.docs);
      }
    } catch (err) {
      console.error('❌ Error fetching partners:', err);
    }
  };

  const fetchLenderPanels = async () => {
    try {
      const res = await postRequest('private/lenderPanelList', { page: 1, limit: 100 }, authCtx.token);
      if (res?.status === 200 && res?.data?.docs) {
        setLenderPanels(res.data.docs);
      }
    } catch (err) {
      console.error('❌ Error fetching lender panels:', err);
    }
  };

  useEffect(() => {
    if (lead_id) {
      fetchAndAutoFillData();
    }
    fetchPartners();
    fetchLenderPanels();
  }, [lead_id]);

  // Debug: Log sellers state changes
  useEffect(() => {
    console.log('🔔 SELLERS STATE CHANGED:', sellers.length, sellers);
  }, [sellers]);

  // Debug: Log purchasers state changes
  useEffect(() => {
    console.log('🔔 PURCHASERS STATE CHANGED:', purchasers.length, purchasers);
  }, [purchasers]);

  const onSubmit = async (formData) => {
    // Validation based on service type
    const serviceType = leadData?.service_type || '';
    const isSale = serviceType === 'Sale' || serviceType === 'Purchase_Sale';
    const isPurchase = serviceType === 'Purchase' || serviceType === 'Purchase_Sale';

    if (isSale && sellers.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Please add at least one seller'
      });
      return;
    }

    if (isPurchase && purchasers.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Please add at least one purchaser'
      });
      return;
    }

    try {
      setLoading(true);

      // Prepare customer IDs based on service type
      let customerIds = [];
      if (isPurchase) {
        customerIds = [...purchasers.map(p => p._id)];
      } else if (isSale) {
        customerIds = [...sellers.map(s => s._id)];
      }

      // Prepare payload
      const payload = {
        lead_id: lead_id,
        product_id: product_id,
        customer_ids: customerIds,
        
        // Property address
        property_address: {
          address1: formData.line1,
          address2: formData.line2,
          town: formData.town,
          county: formData.county,
          postcode: formData.postcode
        },
        
        property_value: parseFloat(formData.purchasePrice) || 0,
        net_value: parseFloat(formData.purchasePrice) || 0,
        ownership_type: formData.ownershipType,
        completion_date: formData.completionDate,

        // Partner and Lender Panel
        partner_id: selectedPartnerId || undefined,
        lender_panel_id: selectedLenderPanelId || undefined,

        // All info data
        ...infoData,
        gifted_deposit: formData.giftedDeposit
      };

      console.log('💾 Updating conveyancing lead with payload:', payload);

      const res = await postRequest(`private/lead-edit/${lead_id}`, payload, authCtx.token);
      
      if (res?.status === 'success') {
        // Also update conveyancing details using safe API
        const conveyancingPayload = {
          lead_id: lead_id,
          propertyDetails: {
            address: {
              line1: formData.line1,
              line2: formData.line2,
              town: formData.town,
              county: formData.county,
              postcode: formData.postcode
            },
            purchasePrice: formData.purchasePrice,
            ownershipType: formData.ownershipType,
            completionDate: formData.completionDate
          },
          info: {
            ...infoData,
            giftedDeposit: formData.giftedDeposit
          },
          sellers: sellers,
          purchasers: purchasers
        };

        // Try safer conveyancing save APIs
        try {
          console.log('💾 Saving conveyancing details with payload:', conveyancingPayload);
          
          // Try multiple save endpoints that work with string IDs
          const saveAttempts = [
            { url: `private/saveConveyancingDetails`, payload: conveyancingPayload, name: 'saveConveyancingDetails POST' },
            { url: `private/updateConveyancingDetails`, payload: conveyancingPayload, name: 'updateConveyancingDetails' },
            { url: `private/conveyancing-save`, payload: conveyancingPayload, name: 'conveyancing-save' }
          ];
          
          let saveSuccess = false;
          
          for (const attempt of saveAttempts) {
            try {
              console.log(`🔄 Trying save API: ${attempt.name}`);
              const saveRes = await postRequest(attempt.url, attempt.payload, authCtx.token);
              console.log(`📋 ${attempt.name} response:`, saveRes);
              
              if (saveRes?.status === 200 || saveRes?.status === 'success') {
                console.log(`✅ Conveyancing details saved via ${attempt.name}`);
                saveSuccess = true;
                break;
              }
            } catch (err) {
              console.log(`❌ ${attempt.name} failed:`, err.message);
            }
          }
          
          if (saveSuccess) {
            console.log('✅ Conveyancing details saved successfully');
          } else {
            console.log('⚠️ Conveyancing details save failed, but main lead updated');
          }
        } catch (saveErr) {
          console.log('⚠️ Error saving conveyancing details:', saveErr.message);
        }

        Swal.fire('Success', 'Conveyancing details updated successfully!', 'success');
        navigate('/leads');
      } else {
        Swal.fire('Error', res?.message || 'Update failed', 'error');
      }
    } catch (err) {
      console.error('Update error:', err);
      Swal.fire('Error', err?.message || 'Something went wrong', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Loading lead data...</Typography>
          <div>Please wait while we fetch the existing information.</div>
        </Paper>
      </Box>
    );
  }

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Leads', path: '/leads' },
          { label: 'Edit Conveyancing', path: '' },
        ]}
      />
      <br />
      
      <Paper elevation={0} sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
        <Typography variant="h4" sx={{ mb: 1, color: '#666', fontWeight: 'normal' }}>
          Edit Conveyancing #{lead_id} - {leadData?.service_type?.replace(/_/g, ' ') || 'Loading...'}
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: '#999', backgroundColor: '#e9ecef', p: 1, borderRadius: 1 }}>
          {leadData?.service_type === 'Sale' ? 'Sale' :
           leadData?.service_type === 'Purchase_Sale' ? 'Purchase & Sale' :
           leadData?.service_type === 'Transfer_of_Equity' ? 'Transfer of Equity' :
           leadData?.service_type === 'Remortgage' ? 'Remortgage' :
           leadData?.service_type === 'Mortgage_Redemption' ? 'Mortgage Redemption' :
           leadData?.service_type === 'First_Registration' ? 'First Registration' :
           leadData?.service_type === 'Review_Legal_Pack' ? 'Review of Legal Pack' :
           'Purchase'} Details
        </Typography>

        {/* DEBUG: Show current state */}
        <Box sx={{ mb: 2, p: 2, backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
            🔍 DEBUG INFO:
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            Service Type: {leadData?.service_type || 'Not loaded'}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            Sellers Count: {sellers.length}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            Purchasers Count: {purchasers.length}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            Lead Data Loaded: {leadData ? 'Yes' : 'No'}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            Customer IDs in Lead: {leadData?.customer_ids?.length || 0}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            Selected Partner ID: {selectedPartnerId || 'None'}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            Selected Lender Panel ID: {selectedLenderPanelId || 'None'}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            Partners Loaded: {partners.length}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            Lender Panels Loaded: {lenderPanels.length}
          </Typography>
        </Box>
        
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Property Section */}
          <Box sx={{ mb: 4, backgroundColor: 'white', p: 3, borderRadius: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
              Property
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>Address</Typography>
                <Controller
                  name="line1"
                  control={control}
                  rules={{ required: 'Address Line 1 is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder="Address Line 1"
                      variant="outlined"
                      size="small"
                      sx={{ mb: 1 }}
                      error={!!errors.line1}
                      helperText={errors.line1?.message}
                    />
                  )}
                />
                <Controller
                  name="line2"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder="Address Line 2"
                      variant="outlined"
                      size="small"
                      sx={{ mb: 1 }}
                    />
                  )}
                />
                <Controller
                  name="town"
                  control={control}
                  rules={{ required: 'Town is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder="Town"
                      variant="outlined"
                      size="small"
                      sx={{ mb: 1 }}
                      error={!!errors.town}
                      helperText={errors.town?.message}
                    />
                  )}
                />
                <Controller
                  name="county"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder="County"
                      variant="outlined"
                      size="small"
                      sx={{ mb: 1 }}
                    />
                  )}
                />
                <Controller
                  name="postcode"
                  control={control}
                  rules={{ required: 'Postcode is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      placeholder="Postcode"
                      variant="outlined"
                      size="small"
                      error={!!errors.postcode}
                      helperText={errors.postcode?.message}
                    />
                  )}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>Purchase Price</Typography>
                    <Controller
                      name="purchasePrice"
                      control={control}
                      rules={{ required: 'Purchase Price is required' }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          InputProps={{
                            startAdornment: <InputAdornment position="start">£</InputAdornment>,
                          }}
                          variant="outlined"
                          size="small"
                          error={!!errors.purchasePrice}
                          helperText={errors.purchasePrice?.message}
                        />
                      )}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>Ownership Type</Typography>
                    <Controller
                      name="ownershipType"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small">
                          <Select {...field} displayEmpty>
                            <MenuItem value="">Select ownership type</MenuItem>
                            <MenuItem value="Freehold">Freehold</MenuItem>
                            <MenuItem value="Leasehold">Leasehold</MenuItem>
                           
                            <MenuItem value="Commonhold">Commonhold</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>Est. Completion Date</Typography>
                    <Controller
                      name="completionDate"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="date"
                          variant="outlined"
                          size="small"
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>Partner</Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={selectedPartnerId}
                        onChange={(e) => setSelectedPartnerId(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="">
                          <em>Select Partner</em>
                        </MenuItem>
                        {partners.map((partner) => (
                          <MenuItem key={partner._id} value={partner._id}>
                            {partner.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>Lender Panel</Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={selectedLenderPanelId}
                        onChange={(e) => setSelectedLenderPanelId(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="">
                          <em>Select Lender Panel</em>
                        </MenuItem>
                        {lenderPanels.map((lender) => (
                          <MenuItem key={lender._id} value={lender._id}>
                            {lender.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>

          {/* Sellers Section - ALWAYS VISIBLE FOR DEBUGGING */}
          <Box sx={{ mb: 4, backgroundColor: 'white', p: 3, borderRadius: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#dc3545' }}>
              🔴 Sellers ({sellers.length}) - DEBUG MODE
            </Typography>

            {/* Always show debug info */}
            <Box sx={{ mb: 2, p: 2, backgroundColor: '#fff3cd', border: '2px solid #ffc107', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                🔍 SELLERS DEBUG INFO:
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                Lead ID: {lead_id}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                Service Type: {leadData?.service_type || 'NOT LOADED'}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                Sellers Array Length: {sellers.length}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                Lead Data Loaded: {leadData ? 'YES' : 'NO'}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                Customer IDs: {leadData?.customer_ids?.length || 0}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                Customers: {leadData?.customers?.length || 0}
              </Typography>
            </Box>

            {sellers.length === 0 && (
              <Box sx={{ mb: 2, p: 2, backgroundColor: '#f8d7da', border: '1px solid #f5c2c7', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: '#842029' }}>
                  ⚠️ No sellers loaded - Check debug info above
                </Typography>
              </Box>
            )}

              {/* Seller Cards - Matching Purchase Form Style */}
              {sellers.map((seller) => (
                <Box
                  key={seller._id}
                  sx={{
                    mb: 2,
                    p: 2,
                    backgroundColor: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: '#333' }}>
                      {seller.title} {seller.forename} {seller.surname}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ color: '#007bff', fontSize: '0.875rem' }}>
                        📧
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                        {seller.email}
                      </Typography>
                      <Typography variant="body2" sx={{ ml: 2, color: '#666', fontSize: '0.875rem' }}>
                        📞 {seller.phone_number}
                      </Typography>
                    </Box>
                  </Box>
                  <CloseIcon
                    sx={{
                      cursor: 'pointer',
                      color: '#dc3545',
                      fontSize: '20px',
                      '&:hover': { color: '#c82333' }
                    }}
                    onClick={() => removeSeller(seller._id)}
                  />
                </Box>
              ))}

              {/* Add Seller Button */}
              <Button
                variant="text"
                onClick={() => setShowSellerModal(true)}
                sx={{
                  color: '#6f42c1',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  padding: '4px 8px',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    textDecoration: 'underline'
                  }
                }}
              >
                + ADD SELLER
              </Button>
            </Box>

          {/* Purchasers Section - ALWAYS VISIBLE FOR DEBUGGING */}
          <Box sx={{ mb: 4, backgroundColor: 'white', p: 3, borderRadius: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#0d6efd' }}>
              🔵 Purchasers ({purchasers.length}) - DEBUG MODE
            </Typography>

            {/* Always show debug info */}
            <Box sx={{ mb: 2, p: 2, backgroundColor: '#d1ecf1', border: '2px solid #0dcaf0', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                🔍 PURCHASERS DEBUG INFO:
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                Lead ID: {lead_id}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                Service Type: {leadData?.service_type || 'NOT LOADED'}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                Purchasers Array Length: {purchasers.length}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                Lead Data Loaded: {leadData ? 'YES' : 'NO'}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                Customer IDs: {leadData?.customer_ids?.length || 0}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                Customers: {leadData?.customers?.length || 0}
              </Typography>
            </Box>

            {purchasers.length === 0 && (
              <Box sx={{ mb: 2, p: 2, backgroundColor: '#f8d7da', border: '1px solid #f5c2c7', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: '#842029' }}>
                  ⚠️ No purchasers loaded - Check debug info above
                </Typography>
              </Box>
            )}

              {/* Purchaser Cards - Matching Purchase Form Style */}
              {purchasers.map((purchaser) => (
                <Box
                  key={purchaser._id}
                  sx={{
                    mb: 2,
                    p: 2,
                    backgroundColor: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: '#333' }}>
                      {purchaser.title} {purchaser.forename} {purchaser.surname}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ color: '#007bff', fontSize: '0.875rem' }}>
                        📧
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                        {purchaser.email}
                      </Typography>
                      <Typography variant="body2" sx={{ ml: 2, color: '#666', fontSize: '0.875rem' }}>
                        📞 {purchaser.phone_number}
                      </Typography>
                    </Box>
                  </Box>
                  <CloseIcon
                    sx={{
                      cursor: 'pointer',
                      color: '#dc3545',
                      fontSize: '20px',
                      '&:hover': { color: '#c82333' }
                    }}
                    onClick={() => removePurchaser(purchaser._id)}
                  />
                </Box>
              ))}

              {/* Add Purchaser Button */}
              <Button
                variant="text"
                onClick={() => setShowCustomerModal(true)}
                sx={{
                  color: '#6f42c1',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  padding: '4px 8px',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    textDecoration: 'underline'
                  }
                }}
              >
                + ADD PURCHASER
              </Button>
            </Box>

          {/* Info Section */}
          <Box sx={{ mb: 4, backgroundColor: 'white', p: 3, borderRadius: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
              Info
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {[
                { key: 'firstTimeBuyer', label: 'First Time Buyer' },
                { key: 'helpToBuyISA', label: 'Help to Buy ISA' },
                { key: 'helpToBuy', label: 'Help to Buy' },
                { key: 'helpToBuyEquityLoan', label: 'Help to Buy Equity Loan' },
                { key: 'rightToBuy', label: 'Right to Buy' },
                { key: 'sharedOwnership', label: 'Shared Ownership' },
                { key: 'buyingWithMortgage', label: 'Buying with Mortgage' },
                { key: 'newBuild', label: 'New Build' },
                { key: 'islamicMortgage', label: 'Islamic Mortgage' }
              ].map((item) => (
                <Chip
                  key={item.key}
                  label={item.label}
                  variant={infoData[item.key] ? 'filled' : 'outlined'}
                  clickable
                  onClick={() => toggleInfoData(item.key)}
                  color={infoData[item.key] ? 'primary' : 'default'}
                  sx={{ 
                    m: 0.5,
                    backgroundColor: infoData[item.key] ? '#1976d2' : '#f5f5f5',
                    color: infoData[item.key] ? 'white' : '#666',
                    '&:hover': {
                      backgroundColor: infoData[item.key] ? '#1565c0' : '#eeeeee'
                    }
                  }}
                />
              ))}
            </Box>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {[
                { key: 'auctionRepossession', label: 'Auction / Repossession' },
                { key: 'multipleProperties', label: 'Multiple Properties' },
                { key: 'buyToLet', label: 'Buy to Let' },
                { key: 'arrangingIndemnityPolicy', label: 'Arranging an Indemnity Policy' },
                { key: 'unregisteredProperty', label: 'Unregistered Property' },
                { key: 'staircasing', label: 'Staircasing' },
                { key: 'isNonUKResident', label: 'Is Non-UK Resident' },
                { key: 'nonResidentialMixed', label: 'Non-Residential / Mixed Use' }
              ].map((item) => (
                <Chip
                  key={item.key}
                  label={item.label}
                  variant={infoData[item.key] ? 'filled' : 'outlined'}
                  clickable
                  onClick={() => toggleInfoData(item.key)}
                  color={infoData[item.key] ? 'secondary' : 'default'}
                  sx={{ 
                    m: 0.5,
                    backgroundColor: infoData[item.key] ? '#9c27b0' : '#f5f5f5',
                    color: infoData[item.key] ? 'white' : '#666',
                    '&:hover': {
                      backgroundColor: infoData[item.key] ? '#7b1fa2' : '#eeeeee'
                    }
                  }}
                />
              ))}
            </Box>
            
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label="Gifted Deposit - Num Giftors"
                variant="outlined"
                sx={{ backgroundColor: '#f5f5f5', color: '#666' }}
              />
              <Controller
                name="giftedDeposit"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    size="small"
                    sx={{ width: '80px' }}
                    onChange={(e) => {
                      field.onChange(e);
                      setInfoData(prev => ({ ...prev, giftedDeposit: parseInt(e.target.value) || 0 }));
                    }}
                  />
                )}
              />
            </Box>
          </Box>

          {/* Save Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{ 
                backgroundColor: '#dc3545',
                '&:hover': { backgroundColor: '#c82333' },
                textTransform: 'none',
                px: 4
              }}
            >
              Save
            </Button>
          </Box>
        </form>

        {/* Customer Search Modal - For Purchasers */}
        <CustomerSearchModal
          show={showCustomerModal}
          onHide={() => setShowCustomerModal(false)}
          authToken={authCtx.token}
          onCustomerAdd={handleCustomerAdd}
          selectedCustomers={purchasers}
          title="Add Purchaser"
        />

        {/* Seller Search Modal */}
        <CustomerSearchModal
          show={showSellerModal}
          onHide={() => setShowSellerModal(false)}
          authToken={authCtx.token}
          onCustomerAdd={handleSellerAdd}
          selectedCustomers={sellers}
          title="Add Seller"
        />
      </Paper>
    </>
  );
};

export default EditConveyancing;